/**
 * The single gate every picked upload passes through. Despite the module name it both validates and
 * *transforms*: photo downscaling lives in ./imageCompression and is orchestrated here, because the
 * size limit is only meaningful once a phone-camera frame has been re-encoded.
 */
import Swal from '../extras/js/sweetalert/sweetalert2.min';
import {isCompressibleImage, isServerReadableImage, prepareImageForUpload} from './imageCompression';

export const MAX_UPLOAD_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const IMAGE_TYPES = ['image/'];
export const IMAGE_OR_PDF_TYPES = ['image/', 'application/pdf'];

/** Default name for the thing being uploaded, used in refusal messages. */
const DEFAULT_SUBJECT = 'file';

/**
 * Validates a picked file and, when it is a photo, downscales it before the size limit is applied.
 *
 * A phone camera routinely writes more than the whole 5MB limit in a single frame, so checking the
 * picked bytes first would reject photos this app can shrink to a few hundred kilobytes. The order
 * is therefore deliberate: type, then downscale, then size.
 *
 * Licence sides pass IMAGE_TYPES: the two sides are merged into a single image server-side, which
 * cannot read a PDF — a PDF side would otherwise be dropped from the merged document unnoticed.
 *
 * @param {File} file the picked file
 * @param {Event} [event] the change event, whose input is cleared when the file is refused
 * @param {string[]} acceptedTypes content-type prefixes to accept
 * @param {object} [options] `subject` names what is being uploaded in refusal messages, e.g.
 *                 "vehicle photo"; `silent` collects the reason instead of showing a dialog
 * @returns {Promise<File|null>} the file to upload — possibly re-encoded — or null when refused
 */
export async function preparePickedFile(file, event, acceptedTypes = IMAGE_OR_PDF_TYPES, options = {}) {
  if (!file) return null;

  const subject = options.subject || DEFAULT_SUBJECT;
  const deny = (title, text) => {
    if (options.silent) {
      options.silent.push(text);
      return null;
    }
    refuse(title, text, event);
    return null;
  };

  const isValidType = acceptedTypes.some((t) => file.type.startsWith(t));
  if (!isValidType) {
    return deny(
        'Invalid File Type',
        acceptedTypes === IMAGE_TYPES
            ? `"${file.name}" is not a photo. Please upload an image for your ${subject}.`
            : `"${file.name}" is not an image or PDF.`);
  }

  const prepared = isCompressibleImage(file) ? await prepareImageForUpload(file) : file;

  // The photo came back untouched and in a format the server cannot read, which means this browser
  // could not decode it either — an iPhone set to "High Efficiency" is the usual cause. Say so now
  // rather than letting the upload succeed and the document arrive unreadable.
  if (isCompressibleImage(prepared) && !isServerReadableImage(prepared)) {
    return deny(
        'Photo Format Not Supported',
        `We could not read "${file.name}". Please upload a JPEG, PNG, GIF, BMP or TIFF. If you ` +
        'are on an iPhone, set Settings → Camera → Formats to "Most Compatible" and take the ' +
        'photo again, or upload a screenshot of it instead.');
  }

  if (prepared.size > MAX_UPLOAD_FILE_SIZE) {
    // An oversized photo that came back byte-identical was never re-encoded at all — the browser
    // declined, which on iOS means it has run out of canvas memory after several photos in a row.
    // Telling that driver their photo is too big sends them off to shrink a file that was fine.
    if (isCompressibleImage(prepared) && prepared === file) {
      return deny(
          'Could Not Process Photo',
          `We could not process "${file.name}" on this device. Try adding photos one at a time, ` +
          'or reload the page and try again.');
    }
    return deny(
        'File Too Large',
        `"${file.name}" is still larger than 5MB after compression. Please upload a smaller file.`);
  }

  return prepared;
}

/**
 * Shows the refusal and clears the input that produced it.
 *
 * @param {string} title the alert title
 * @param {string} text the alert body
 * @param {Event} [event] the change event, whose input is cleared when one was supplied
 */
function refuse(title, text, event) {
  Swal.fire({title, text, icon: 'error', confirmButtonColor: '#3085d6'});
  if (event?.target) event.target.value = '';
}
